import React, { useCallback, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";

import { broadcastsApi } from "../../../api/broadcastsApi";
import { errorMessage } from "../tenancy/useOperators";
import {
  EXCLUDE_LABELS,
  FILE_PROBLEM_REASONS,
  IBroadcast,
  IBroadcastPreview,
  IPreparedRow,
} from "../../../types/models/broadcasts";

/**
 * Importing a contact list.
 *
 * The file is read and reported on *before* anything is stored, and that is the
 * point of the screen rather than a nicety. A real HubSpot export of qualified
 * leads had 40 rows with no phone number, two duplicates and numbers from three
 * countries in seven different formats. Discovering that in a delivery report
 * afterwards is the failure mode this exists to prevent — so the counts are the
 * headline, the bad rows are visible, and nothing is created until someone has
 * looked at both.
 */

const Container = styled.div`
  .counts {
    margin-bottom: 16px;
  }

  .ant-statistic-content {
    font-size: 24px;
  }

  .problem {
    color: #cf1322;
  }

  .muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .row-bad td {
    background: #fff1f0;
  }
`;

/** `hubspot-crm-exports-vail-qualified-leads-2026-09-11.csv` -> a sane default. */
const nameFromFile = (filename: string) =>
  filename.replace(/\.csv$/i, "").replace(/[-_]+/g, " ").trim().slice(0, 160);

const previewColumns = [
  { title: "Row", dataIndex: "row_number", width: 64 },
  { title: "Name", dataIndex: "name_raw", ellipsis: true },
  {
    title: "Greeting",
    dataIndex: "greeting",
    width: 130,
    // Shown next to the raw name rather than instead of it: this is the value
    // that ends up in `Hola {{1}}`, and `VERONICA HERNANDEZ` becoming
    // `Veronica` is a change someone should see before several hundred
    // messages go out with it.
    render: (text: string) => text || <span className="muted">—</span>,
  },
  { title: "From the file", dataIndex: "phone_raw", width: 170, ellipsis: true },
  {
    title: "Will send to",
    dataIndex: "phone_e164",
    width: 160,
    render: (text: string, row: IPreparedRow) =>
      text ? (
        <span>
          {text}{" "}
          {row.phone_region && <Tag className="muted">{row.phone_region}</Tag>}
        </span>
      ) : (
        <span className="problem">
          {EXCLUDE_LABELS[row.exclude_reason as keyof typeof EXCLUDE_LABELS] ??
            "Not sendable"}
        </span>
      ),
  },
  {
    title: "",
    dataIndex: "exclude_reason",
    width: 110,
    render: (reason: string) =>
      reason === "duplicate" ? <Tag color="orange">Duplicate</Tag> : null,
  },
];

function ImportWizard({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (broadcast: IBroadcast) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [preview, setPreview] = useState<IBroadcastPreview | null>(null);
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  // Only tracked once someone overrides the guess. Before that the server's
  // detection wins, including when it re-detects after a different file.
  const [columns, setColumns] = useState<{ name_column?: string; phone_column?: string }>({});

  const reset = () => {
    setFile(null);
    setName("");
    setPreview(null);
    setColumns({});
  };

  const read = useCallback(
    (target: File, override: { name_column?: string; phone_column?: string }) => {
      setReading(true);
      broadcastsApi
        .preview(target, override)
        .then(setPreview)
        .catch((error) => {
          setPreview(null);
          // 422 carries a sentence written for the operator ("That file has no
          // header row"), which is far more useful than a generic failure.
          message.error(
            error?.response?.data?.detail ?? "That file could not be read."
          );
        })
        .finally(() => setReading(false));
    },
    []
  );

  const onFile = (chosen: File) => {
    setFile(chosen);
    setName(nameFromFile(chosen.name));
    setColumns({});
    read(chosen, {});
    // Returning false keeps antd from uploading it itself — the file goes to
    // our own endpoint, not to Upload's action URL.
    return false;
  };

  const onColumnChange = (patch: { name_column?: string; phone_column?: string }) => {
    const next = { ...columns, ...patch };
    setColumns(next);
    if (file) read(file, next);
  };

  const onImport = () => {
    if (!file || !name.trim()) return;
    setImporting(true);
    broadcastsApi
      .importList(file, name.trim(), columns)
      .then((broadcast) => {
        message.success(
          `Imported ${broadcast.counts.sendable} contacts from ${broadcast.source_filename}`
        );
        reset();
        onCreated(broadcast);
      })
      // A tenancy refusal comes back as `{"operator": "..."}` rather than
      // `detail`, so read the body properly instead of falling through to a
      // generic failure that says nothing about what to do next.
      .catch((error) =>
        message.error(errorMessage(error, "That list could not be imported."))
      )
      .finally(() => setImporting(false));
  };

  const counts = preview?.counts;
  const problems = counts
    ? FILE_PROBLEM_REASONS.reduce((total, reason) => total + (counts[reason] ?? 0), 0)
    : 0;

  return (
    <Modal
      title="New broadcast"
      visible={open}
      width={980}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="import"
          type="primary"
          loading={importing}
          disabled={
            !preview ||
            !name.trim() ||
            (counts?.sendable ?? 0) === 0
          }
          onClick={onImport}
        >
          {counts
            ? `Import ${counts.sendable} contacts`
            : "Import"}
        </Button>,
      ]}
    >
      <Container>
        <Upload.Dragger
          accept=".csv,text/csv"
          maxCount={1}
          beforeUpload={onFile}
          onRemove={reset}
          className="uploader"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop the CSV export here</p>
          <p className="ant-upload-hint">
            Straight from HubSpot is fine — it needs a name column and a phone
            column, and the rest is ignored.
          </p>
        </Upload.Dragger>

        {reading && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin tip="Reading the file…" />
          </div>
        )}

        {preview && !reading && (
          <>
            <Form layout="vertical" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={10}>
                  <Form.Item label="List name" required>
                    <Input
                      value={name}
                      maxLength={160}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Vail qualified leads"
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    label="Name column"
                    // The guess is right nearly every time on a HubSpot export.
                    // Shown anyway, because when it is wrong the alternative is
                    // an import that greets everybody by their email address.
                    extra={<span className="muted">Used for the greeting</span>}
                  >
                    <Select
                      value={preview.name_column || undefined}
                      onChange={(value) => onColumnChange({ name_column: value })}
                      options={preview.headers.map((h) => ({ label: h, value: h }))}
                      placeholder="None"
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item label="Phone column" required>
                    <Select
                      value={preview.phone_column || undefined}
                      onChange={(value) => onColumnChange({ phone_column: value })}
                      options={preview.headers.map((h) => ({ label: h, value: h }))}
                      placeholder="None"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Row gutter={16} className="counts">
              <Col span={6}>
                <Statistic title="Rows in the file" value={counts!.total} />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Ready to send"
                  value={counts!.sendable}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Can’t be sent to"
                  value={problems}
                  valueStyle={problems ? { color: "#cf1322" } : undefined}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Countries"
                  value={Object.entries(preview.regions)
                    .map(([region, n]) => `${region} ${n}`)
                    .join("  ")}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>

            {problems > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`${problems} of ${counts!.total} rows will not be sent to`}
                description={
                  <>
                    {FILE_PROBLEM_REASONS.filter((reason) => counts![reason] > 0).map(
                      (reason) => (
                        <Tag key={reason} color="red">
                          {EXCLUDE_LABELS[reason]}: {counts![reason]}
                        </Tag>
                      )
                    )}
                    <div style={{ marginTop: 8 }}>
                      They are imported anyway, marked as excluded — so the
                      totals still account for every line of the file, and a
                      number with a typo in it can be fixed by hand afterwards.
                    </div>
                  </>
                }
              />
            )}

            {Object.keys(preview.regions).length > 1 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="This list spans more than one country"
                description={
                  "A WhatsApp template is approved per language. Check the one " +
                  "you pick makes sense for everybody on the list."
                }
              />
            )}

            <Table<IPreparedRow>
              size="small"
              rowKey="row_number"
              dataSource={preview.rows}
              columns={previewColumns as any}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              rowClassName={(row) => (row.status === "excluded" ? "row-bad" : "")}
              scroll={{ x: 800 }}
            />
          </>
        )}
      </Container>
    </Modal>
  );
}

export default ImportWizard;
